import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { trackErrorEvent, trackEvent } from "../utils/analytics";

export interface UniversalLinkData {
	path: string;
	params: Record<string, string>;
	environment: "development" | "staging" | "production";
}

export class UniversalLinkService {
	private static instance: UniversalLinkService;
	private listeners: ((data: UniversalLinkData) => void)[] = [];
	private pendingLaunchUrl: string | null = null; // Store launch URL if no listeners yet

	private constructor() {
		this.initializeAppUrlListener();
	}

	public static getInstance(): UniversalLinkService {
		if (!UniversalLinkService.instance) {
			UniversalLinkService.instance = new UniversalLinkService();
		}
		return UniversalLinkService.instance;
	}

	private getEnvironmentFromUrl(url: string): "development" | "staging" | "production" {
		if (url.includes("localhost") || url.includes("127.0.0.1")) {
			return "development";
		}
		if (url.includes("staging.inhouse.health")) {
			return "staging";
		}
		return "production";
	}

	private async initializeAppUrlListener() {
		if (Capacitor.isNativePlatform()) {
			// Listen for app URL opens
			App.addListener("appUrlOpen", (data: { url: string }) => {
				this.handleUniversalLink(data.url);
			});

			// Check if app was opened with a URL
			const ret = await App.getLaunchUrl();
			if (ret?.url) {
				// If we have listeners, process immediately
				// Otherwise, store it to process when listeners are added
				if (this.listeners.length > 0) {
					this.handleUniversalLink(ret.url);
				} else {
					this.pendingLaunchUrl = ret.url;
				}
			}
		}
	}

	private handleUniversalLink(url: string) {
		try {
			const urlObj = new URL(url);
			const path = urlObj.pathname;
			const params = Object.fromEntries(urlObj.searchParams.entries());
			const environment = this.getEnvironmentFromUrl(url);

			const linkData: UniversalLinkData = {
				path,
				params,
				environment,
			};

			trackEvent("Universal link received:", linkData);

			// Notify all listeners
			this.listeners.forEach((listener) => listener(linkData));
		} catch (error) {
			trackErrorEvent("Error parsing universal link", error, {
				action: "Error parsing universal link",
			});
		}
	}

	public addListener(callback: (data: UniversalLinkData) => void) {
		this.listeners.push(callback);

		// If there's a pending launch URL, process it now that we have a listener
		if (this.pendingLaunchUrl) {
			const url = this.pendingLaunchUrl;
			this.pendingLaunchUrl = null; // Clear it before processing to avoid double-processing
			this.handleUniversalLink(url);
		}
	}

	public removeListener(callback: (data: UniversalLinkData) => void) {
		const index = this.listeners.indexOf(callback);
		if (index > -1) {
			this.listeners.splice(index, 1);
		}
	}

	public parseUniversalLink(url: string): UniversalLinkData | null {
		try {
			const urlObj = new URL(url);
			const path = urlObj.pathname;
			const params = Object.fromEntries(urlObj.searchParams.entries());
			const environment = this.getEnvironmentFromUrl(url);

			return {
				path,
				params,
				environment,
			};
		} catch (error) {
			trackErrorEvent("Error parsing universal link", error, {
				action: "Error parsing universal link",
			});
			return null;
		}
	}

	public generateUniversalLink(path: string, params: Record<string, string> = {}, environment?: "development" | "staging" | "production"): string {
		const env = environment || this.getCurrentEnvironment();
		const baseUrl = this.getBaseUrlForEnvironment(env);
		const url = new URL(path, baseUrl);

		Object.entries(params).forEach(([key, value]) => {
			url.searchParams.set(key, value);
		});

		return url.toString();
	}

	private getCurrentEnvironment(): "development" | "staging" | "production" {
		const env: string = import.meta.env.VITE_ENVIROMENT || "development";
		return env as "development" | "staging" | "production";
	}

	private getBaseUrlForEnvironment(environment: "development" | "staging" | "production"): string {
		switch (environment) {
			case "development":
				return "http://localhost:3000";
			case "staging":
				return "https://staging.inhouse.health";
			case "production":
				return "https://app.inhouse.health";
		}
	}

	public hasPendingLaunchUrl(): boolean {
		return this.pendingLaunchUrl !== null;
	}
}

export const universalLinkService = UniversalLinkService.getInstance();
